'use client';

import { Children, useRef, useState, ReactNode, useLayoutEffect, useId } from 'react';
import clsx from 'clsx';
import { Component } from '@headless-workspace/typings';
import { GlowIcon } from '../../atoms';
import { GlowButton } from '../../molecules/cta';
import { Icons } from '../../res/icons';

const SCROLLBAR_WIDTH_PERCENT = 20;
const SCROLLBAR_MAX_LEFT_POSITION_PERCENT = 100 - SCROLLBAR_WIDTH_PERCENT;

type ProgressBarProps = { ariaLabelProgressBar: string; scrollProgress: number };

const ProgressBar: Component<ProgressBarProps> = ({ ariaLabelProgressBar, scrollProgress }) => {
    const left = Math.min(
        SCROLLBAR_MAX_LEFT_POSITION_PERCENT,
        Math.max(0, (scrollProgress * SCROLLBAR_MAX_LEFT_POSITION_PERCENT) / 100),
    );

    return (
        <div
            className={'mx-1 desktop:mx-0 flex items-center h-[1.5rem]'}
            aria-label={ariaLabelProgressBar}
            aria-valuenow={scrollProgress}
            role={'progressbar'}
        >
            <div className={'w-full bg-surface-neutral rounded-infinity h-[0.25rem] relative'}>
                <div
                    className={
                        'absolute bg-surface-brand h-[0.5rem] -top-[0.125rem] rounded-infinity transition-all ease-out'
                    }
                    style={{ width: `${SCROLLBAR_WIDTH_PERCENT}%`, left: `${left}%` }}
                />
            </div>
        </div>
    );
};

export type GlowCarouselProps = {
    ariaLabels: {
        carousel: string;
        prev: string;
        next: string;
        product: string;
        productList: string;
        progressBar: string;
    };
    children: ReactNode;
    className?: string;
    navigationButtonClassName?: string;
    productTileId: string;
    removeMarginAtViewport?: 'desktopS' | 'desktop';
};

type Direction = 'left' | 'right';

const removeMarginCSS = {
    desktop: 'desktop:first:ml-0 desktop:last:mr-0',
    desktopS: 'desktopS:first:ml-0 desktopS:last:mr-0',
};

export const GlowCarousel: Component<GlowCarouselProps> = ({
    ariaLabels,
    children,
    className,
    productTileId,
    navigationButtonClassName = 'hidden desktop:block',
    removeMarginAtViewport = 'desktop',
}) => {
    const scrollRef = useRef<HTMLUListElement>(null);
    const currentItemInView = useRef(0);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const listId = useId();

    const numberOfItems = Children.count(children);

    const updateScrollState = () => {
        if (!scrollRef.current) return;

        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        if (scrollWidth - clientWidth > 0) {
            setScrollProgress((scrollLeft / (scrollWidth - clientWidth)) * 100);
        }

        const firstItem = scrollRef.current.children[0];
        const itemWidth = firstItem instanceof HTMLElement ? firstItem.offsetWidth : 1;
        currentItemInView.current = Math.floor(scrollLeft / itemWidth); // Index of the first fully visible item in the carousel based on current scroll position
    };

    useLayoutEffect(() => {
        updateScrollState();
        const container = scrollRef.current;

        if (container) {
            container.addEventListener('scroll', updateScrollState, { passive: true });
            return () => container.removeEventListener('scroll', updateScrollState);
        }
    }, []);

    const scroll = (direction: Direction) => {
        if (
            !scrollRef.current ||
            (!canScrollLeft && direction === 'left') ||
            (!canScrollRight && direction === 'right')
        )
            return;

        const containerWidth = scrollRef.current.offsetWidth;
        const firstItem = scrollRef.current.children[0];
        const itemWidth = firstItem instanceof HTMLElement ? firstItem.offsetWidth : 0;
        const itemsInView =
            itemWidth &&
            (containerWidth > itemWidth
                ? Math.floor(containerWidth / itemWidth)
                : Math.round(containerWidth / itemWidth)); // Ensure at least 1 item when the ratio rounds to 0
        const scrollStep = itemsInView > 1 ? itemsInView - 1 : itemsInView; // Scroll by the number of visible items - 1 or by 1 if there is only one item

        if (direction === 'left' && currentItemInView.current >= itemsInView) {
            currentItemInView.current -= scrollStep;
        } else if (direction === 'left') {
            currentItemInView.current = 0; // If we can't move that many items back, scroll to the very first item
        } else if (direction === 'right' && currentItemInView.current < numberOfItems - itemsInView) {
            currentItemInView.current += scrollStep;
        } else if (direction === 'right') {
            currentItemInView.current = numberOfItems - 1; // if we can't move that many items ahead, scroll to the very last item
        }

        const itemToScroll = scrollRef.current.children[currentItemInView.current];

        const { scrollWidth } = scrollRef.current;
        const getItemToScrollOffsetLeft = () => {
            if ((currentItemInView.current === 0 && canScrollLeft) || !(itemToScroll instanceof HTMLElement)) return 0;
            else if (currentItemInView.current === numberOfItems - 1 && canScrollRight) return scrollWidth;
            return itemToScroll.offsetLeft;
        };

        scrollRef.current.scrollTo({ left: getItemToScrollOffsetLeft(), behavior: 'smooth' });
    };

    const showProgressBar = canScrollLeft || canScrollRight;

    const glowButtonCommonProps = (direction: Direction) => ({
        ariaControls: listId,
        Icon: () => <GlowIcon Icon={Icons.ChevronRight} type={'medium'} />,
        onClick: () => scroll(direction),
        size: 'large' as const,
    });

    return (
        <section
            className={clsx('w-full mx-auto flex gap-1 items-center', className)}
            aria-roledescription={ariaLabels.carousel}
        >
            <GlowButton
                {...glowButtonCommonProps('left')}
                ariaLabel={ariaLabels.prev}
                className={clsx('rotate-180', navigationButtonClassName)}
                disabled={!canScrollLeft}
                variant={canScrollLeft ? 'primary' : 'disabled'}
            />
            <div className={'relative flex flex-col gap-1 overflow-hidden'}>
                <ul
                    aria-label={ariaLabels.productList}
                    aria-roledescription={ariaLabels.product}
                    className={'flex flex-row gap-1 overflow-x-auto scrollbar-hide scroll-smooth scroll-touch'}
                    id={listId}
                    ref={scrollRef}
                >
                    {Children.toArray(children).map((child, index) => (
                        <li
                            key={index}
                            aria-labelledby={productTileId} // GlowProductTile.tsx
                            className={clsx(
                                'first:ml-1 last:mr-1 desktop:first:ml-0 desktop:last:mr-0 tablet:snap-start shrink-0',
                                removeMarginCSS[removeMarginAtViewport],
                            )}
                        >
                            {child}
                        </li>
                    ))}
                </ul>
                {showProgressBar && (
                    <ProgressBar ariaLabelProgressBar={ariaLabels.progressBar} scrollProgress={scrollProgress} />
                )}
            </div>
            <GlowButton
                {...glowButtonCommonProps('right')}
                ariaLabel={ariaLabels.next}
                className={navigationButtonClassName}
                disabled={!canScrollRight}
                variant={canScrollRight ? 'primary' : 'disabled'}
            />
        </section>
    );
};
